import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { portalApi } from '@/api/portal'
import { Receipt, AlertCircle, ChevronRight } from 'lucide-react'
import { usePortalMoney } from '@/hooks/useMoney'
import { usePortalDates } from '@/hooks/useDates'

function PaymentBadge({ status }: { status: string }) {
  const map: Record<string, { cls: string; label: string }> = {
    Unpaid:        { cls: 'bg-red-100 text-red-700',    label: 'Unpaid' },
    PartiallyPaid: { cls: 'bg-amber-100 text-amber-700', label: 'Part paid' },
    Paid:          { cls: 'bg-green-100 text-green-700', label: 'Paid' },
    Void:          { cls: 'bg-gray-100 text-gray-500',   label: 'Void' },
  }
  const { cls, label } = map[status] ?? { cls: 'bg-gray-100 text-gray-500', label: status }
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>
}

export default function InvoicesPage() {
  const { formatDate } = usePortalDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  }
  const { fmt: fmtMoney } = usePortalMoney()
  const navigate = useNavigate()
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['portal-invoices'],
    queryFn: () => portalApi.getInvoices().then(r => r.data),
  })

  const totalOutstanding = invoices
    .filter(i => i.paymentStatus === 'Unpaid' || i.paymentStatus === 'PartiallyPaid')
    .reduce((sum, i) => sum + i.amount, 0)

  if (isLoading) return <p className="text-gray-500">Loading…</p>

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt size={22} className="text-[var(--brand-primary)]" />
            Invoices
          </h1>
          <p className="text-sm text-gray-500 mt-1">Invoices issued to your organisation</p>
        </div>
        {totalOutstanding > 0 && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm font-medium shrink-0">
            <AlertCircle size={15} />
            Outstanding: {fmtMoney(totalOutstanding)}
          </div>
        )}
      </div>

      {invoices.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Receipt size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No invoices yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {/* Mobile: card list */}
          <div className="sm:hidden divide-y divide-gray-100">
            {invoices.map(inv => (
              <div
                key={inv.id}
                className="px-4 py-3 cursor-pointer hover:bg-[var(--brand-secondary)]/10 transition-colors"
                onClick={() => navigate(`/portal/invoices/${inv.id}`)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{inv.invoiceNumber}</p>
                    {inv.courseSummary && <p className="text-xs text-gray-500 truncate">{inv.courseSummary}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-gray-900 tabular-nums">{fmtMoney(inv.amount)}</p>
                    <PaymentBadge status={inv.paymentStatus} />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  Issued {fmtDate(inv.issuedDate)}
                  {inv.dueDate && <> · Due {fmtDate(inv.dueDate)}</>}
                </p>
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <table className="hidden sm:table w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Course</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Issued</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Due</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map(inv => (
                <tr
                  key={inv.id}
                  onClick={() => navigate(`/portal/invoices/${inv.id}`)}
                  className="hover:bg-[var(--brand-secondary)]/10 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{inv.invoiceNumber}</p>
                    {inv.bookingDate && (
                      <p className="text-xs text-gray-400">Booking: {fmtDate(inv.bookingDate)}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {inv.courseSummary ?? <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(inv.issuedDate)}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {inv.dueDate ? fmtDate(inv.dueDate) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900 tabular-nums whitespace-nowrap">
                    {fmtMoney(inv.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <PaymentBadge status={inv.paymentStatus} />
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    <ChevronRight size={16} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
